import React from 'react';
import { toast, type ToastOptions } from 'react-toastify';

import LabeledToast, { LabeledToastProps } from '../shared-components/ui/LabeledToast/LabeledToast';
import { getMessageFromUnknown } from '../utils/errorMessage';

const DEFAULT_TOAST_OPTIONS = {
  position: 'top-right',
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'colored',
  ariaLabel: 'toast',
} satisfies ToastOptions;

export const TOAST_LABELS = {
  updateError: 'Update failed',
  updateSuccess: 'Update successful',
  createError: 'Create failed',
  createSuccess: 'Create successful',
  deleteError: 'Delete failed',
  deleteSuccess: 'Delete successful',
  error: 'Error',
};

export type ToastContentInput = string | LabeledToastProps;

const renderContent = (content: ToastContentInput) => (typeof content === 'string' ? content : React.createElement(LabeledToast, content));

const useToast = (baseOptions: ToastOptions = {}) => {
  const base = { ...DEFAULT_TOAST_OPTIONS, ...baseOptions };

  const showError = (content: ToastContentInput, options: ToastOptions = {}) => toast.error(renderContent(content), { ...base, ...options });

  const showSuccess = (content: ToastContentInput, options: ToastOptions = {}) => toast.success(renderContent(content), { ...base, ...options });

  const showInfo = (content: ToastContentInput, options: ToastOptions = {}) => toast.info(renderContent(content), { ...base, ...options });

  const showWarning = (message: string, options: ToastOptions = {}) =>
    toast.warning(renderContent({ message, label: 'Warning' }), { ...base, ...options });

  const fromException = ({ error, label }: { error: unknown; label: string }, options: ToastOptions = {}) =>
    showError({ label, message: getMessageFromUnknown(error) }, options);

  return { showError, showSuccess, showInfo, showWarning, fromException };
};

export default useToast;
